import React, { useEffect, useMemo, useRef, useState } from "react";
import "./GlobalTopBar.css";

const API = "/api/topbar";
const icons = { start: "🚀", book: "▣", calendar: "▦", alerts: "🔔", notify: "🎉", settings: "⚙", help: "?" };

export default function GlobalTopBar({ user, onLogout, onMenuClick }) {
  const [profile, setProfile] = useState({
    display_name: user?.name || user?.display_name || "",
    title: user?.role === "admin" ? "System Administrator" : user?.role === "family" ? "Family Member" : "User",
    photo_data: "",
    preferences: {},
    role: user?.role || "",
  });
  const [menu, setMenu] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [panel, setPanel] = useState(null);
  const timer = useRef(null);
  const fileRef = useRef(null);

  const role = String(profile.role || user?.role || "").toLowerCase();
  const canManage = ["owner", "admin", "administrator", "system admin"].includes(role);

  useEffect(() => {
    fetch(`${API}/profile`)
      .then((r) => (r.ok ? r.json() : null))
      .then((x) => x && setProfile((current) => ({ ...current, ...x })))
      .catch(() => {});
    fetch(`${API}/alerts`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setAlerts)
      .catch(() => {});
    fetch(`${API}/notifications`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setNotifications)
      .catch(() => {});
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const markNotificationRead = (id) => {
    fetch(`${API}/notifications/${id}/read`, { method: "POST" })
      .then(() => setNotifications((cur) => cur.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))))
      .catch(() => {});
  };
  const markAllNotificationsRead = () => {
    fetch(`${API}/notifications/read-all`, { method: "POST" })
      .then(() => setNotifications((cur) => cur.map((n) => ({ ...n, is_read: 1 }))))
      .catch(() => {});
  };

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(() => {
      fetch(`${API}/search?q=${encodeURIComponent(q)}`)
        .then((r) => (r.ok ? r.json() : []))
        .then(setResults)
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const initials = useMemo(
    () =>
      String(profile.display_name || "System Admin")
        .split(/\s+/)
        .map((x) => x[0])
        .slice(0, 2)
        .join("")
        .toUpperCase(),
    [profile.display_name],
  );

  const save = async (patch) => {
    const response = await fetch(`${API}/profile`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (response.ok) setProfile(await response.json());
  };

  const upload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      alert("Please choose an image under 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => save({ photo_data: reader.result });
    reader.readAsDataURL(file);
  };

  const nav = (path) => {
    window.location.href = path;
  };

  const logout = () => {
    setMenu(false);
    if (onLogout) onLogout();
    else window.location.href = "/";
  };

  return (
    <>
      <header className="jy-topbar">
        <button className="jy-back" aria-label="Go back" title="Back" onClick={() => window.history.back()}>←</button>
        <button className="jy-menu" aria-label="Open navigation" title="Navigation" onClick={onMenuClick}>☰</button>
        <div className="jy-search-wrap">
          <span>⌕</span>
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search clients, caregivers, visits, invoices..."
            aria-label="Global search"
          />
          {results.length > 0 && (
            <div className="jy-search-results">
              {results.map((result, index) => (
                <button key={`${result.type || "result"}-${index}`} onClick={() => nav(result.path || "#")}>
                  <b>{result.type}</b><span>{result.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="jy-tools" aria-label="Utility navigation">
          <button onClick={() => nav("/admin/enterprise")}><i>{icons.start}</i><span>Start</span></button>
          <button onClick={() => nav("/admin/clients")}><i>{icons.book}</i><span>Directory</span></button>
          <button onClick={() => nav("/admin/scheduling")}><i>{icons.calendar}</i><span>Calendar</span></button>
          <button onClick={() => setPanel("alerts")} className="jy-alert-btn">
            <i>{icons.alerts}</i>{alerts.length > 0 && <em>{alerts.length}</em>}<span>Alerts</span>
          </button>
          <button onClick={() => setPanel("notifications")} className="jy-alert-btn">
            <i>{icons.notify}</i>{unreadCount > 0 && <em>{unreadCount}</em>}<span>Celebrations</span>
          </button>
          <button onClick={() => nav("/admin/enterprise")}><i>{icons.settings}</i><span>Settings</span></button>
          <button onClick={() => setPanel("help")}><i>{icons.help}</i><span>Help</span></button>
        </nav>

        <button className="jy-profile" onClick={() => setMenu((open) => !open)}>
          {profile.photo_data ? <img src={profile.photo_data} alt="Profile" /> : <span className="jy-avatar">{initials}</span>}
          <span className="jy-profile-text"><b>{profile.display_name || "System Admin"}</b><small>{profile.title || "Owner"}</small></span>
          <span>⌄</span>
        </button>

        {menu && (
          <div className="jy-profile-menu">
            <button onClick={() => setPanel("profile")}>👤 My Profile</button>
            <button onClick={() => setPanel("personalize")}>◉ Personalize Dashboard</button>
            {canManage && <button onClick={() => nav("/admin/enterprise")}>🛡 User Privileges</button>}
            {canManage && <button onClick={() => nav("/admin/enterprise")}>▦ Switch Organization</button>}
            <hr />
            <button className="danger" onClick={logout}>↪ Logout</button>
          </div>
        )}
      </header>

      {panel && (
        <div className="jy-modal-backdrop" onMouseDown={() => setPanel(null)}>
          <section className="jy-modal" onMouseDown={(event) => event.stopPropagation()}>
            <button className="jy-close" onClick={() => setPanel(null)}>×</button>
            {panel === "alerts" && <><h2>Alerts</h2>{alerts.length ? alerts.map((alert, index) => <div className="jy-alert-row" key={index}><b>{alert.title}</b><span>{alert.detail}</span></div>) : <p>No active alerts.</p>}</>}
            {panel === "notifications" && <><h2>🎉 Celebrations & Notifications</h2>{notifications.length > 0 && <button className="jy-primary" onClick={markAllNotificationsRead} style={{marginBottom: "12px"}}>Mark all as read</button>}{notifications.length ? notifications.map((n) => <div className="jy-alert-row" key={n.id} style={{opacity: n.is_read ? 0.55 : 1}} onClick={() => !n.is_read && markNotificationRead(n.id)}><b>{n.title}</b><span>{n.detail}</span></div>) : <p>No notifications yet.</p>}</>}
            {panel === "start" && <><h2>Getting Started</h2><p>Complete company information, users, roles, billing rates, EVV, payroll, notifications, family portal and mobile setup.</p><button className="jy-primary" onClick={() => nav("/admin/enterprise")}>Open setup</button></>}
            {panel === "help" && <><h2>Help Center</h2><p>Access training, release notes and support resources.</p></>}
            {panel === "profile" && <><h2>My Profile</h2><label>Display name<input value={profile.display_name || ""} onChange={(event) => setProfile({ ...profile, display_name: event.target.value })} /></label><label>Title<input value={profile.title || ""} onChange={(event) => setProfile({ ...profile, title: event.target.value })} /></label><input ref={fileRef} hidden type="file" accept="image/*" onChange={upload} /><button onClick={() => fileRef.current?.click()}>Upload Photo</button><button className="jy-primary" onClick={() => save({ display_name: profile.display_name, title: profile.title })}>Save Profile</button></>}
            {panel === "personalize" && <><h2>Personalize Dashboard</h2><label className="jy-check"><input type="checkbox" checked={profile.preferences?.compact_mode || false} onChange={(event) => save({ preferences: { ...profile.preferences, compact_mode: event.target.checked } })} /> Compact dashboard spacing</label><label className="jy-check"><input type="checkbox" checked={profile.preferences?.show_finance !== false} onChange={(event) => save({ preferences: { ...profile.preferences, show_finance: event.target.checked } })} /> Show financial widgets</label><label className="jy-check"><input type="checkbox" checked={profile.preferences?.show_priorities !== false} onChange={(event) => save({ preferences: { ...profile.preferences, show_priorities: event.target.checked } })} /> Show priority panel</label></>}
          </section>
        </div>
      )}
    </>
  );
}
